/**
 * Stadium hoardings: a run of advertising boards along the front of the grandstand and
 * along the infield kerb. Affiliate boards open the tagged Amazon link when clicked;
 * "advertise here" boards open the configured contact; house boards are decoration that
 * fills the run when no advertiser is configured.
 */
import * as THREE from 'three';
import { mulberry32 } from '@decathlon/shared';
import { box } from './blocky.js';
import { STRAIGHT_END, STRAIGHT_START, TRACK_WIDTH } from './stadium.js';
import type { AffiliatePayload } from '../screens/affiliate.js';

export interface BoardInfo {
  label: string;
  url: string | null;
  kind: 'affiliate' | 'advertise' | 'house';
}

const W = 9;
const H = 2.4;
const HOUSE: { label: string; bg: string; fg: string }[] = [
  { label: 'DECATHLON', bg: '#1e88e5', fg: '#ffffff' },
  { label: 'TEN EVENTS · TWO DAYS', bg: '#e53935', fg: '#ffffff' },
  { label: 'ALTERNATE THE KEYS', bg: '#fdd835', fg: '#1b2430' },
  { label: 'REAL IAAF POINTS', bg: '#43a047', fg: '#ffffff' },
  { label: 'THREE LIVES', bg: '#8e24aa', fg: '#ffffff' },
  { label: 'BEAT 8847', bg: '#fb8c00', fg: '#1b2430' },
];

const font = (px: number, weight = 700) => `${weight} ${px}px Fredoka, Nunito, "Segoe UI", system-ui, sans-serif`;

function face(label: string, kind: BoardInfo['kind'], bg: string, fg: string, footer: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 272;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.strokeStyle = '#1b2430';
  ctx.lineWidth = 22;
  ctx.strokeRect(11, 11, c.width - 22, c.height - 22);
  ctx.fillStyle = fg;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let size = 118;
  ctx.font = font(size);
  while (ctx.measureText(label).width > c.width - 140 && size > 56) {
    size -= 6;
    ctx.font = font(size);
  }
  ctx.fillText(label, c.width / 2, footer ? c.height / 2 - 22 : c.height / 2);
  if (footer) {
    ctx.font = font(34, 600);
    ctx.globalAlpha = 0.8;
    ctx.fillText(footer, c.width / 2, c.height - 54);
    ctx.globalAlpha = 1;
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/** Build all hoardings. Returns a group plus the clickable panel meshes. */
export function buildBillboards(ads: AffiliatePayload): { group: THREE.Group; panels: THREE.Mesh[] } {
  const group = new THREE.Group();
  const panels: THREE.Mesh[] = [];
  const rng = mulberry32(777);
  const links = ads.links;
  const items: { info: BoardInfo; bg: string; fg: string; footer: string }[] = [];
  // Front of the grandstand: a continuous run from just before the start to past the finish.
  const slots: { x: number; z: number; rotY: number }[] = [];
  for (let x = STRAIGHT_START + 6; x < STRAIGHT_END - 6; x += W + 0.6) slots.push({ x: x + W / 2, z: TRACK_WIDTH + 2.4, rotY: Math.PI });
  // No infield row: the side camera sits on the infield and boards there block the runners.

  let houseIdx = 0;
  let linkIdx = 0;
  slots.forEach((_, i) => {
    const advertiseSlot = (i + 2) % 6 === 0;
    if (advertiseSlot) items.push({ info: { label: ads.advertise.text, url: ads.advertise.url, kind: 'advertise' }, bg: '#fdd835', fg: '#1b2430', footer: 'Your brand on these hoardings' });
    else if (links.length && (i % 3 !== 1 || links.length > 4)) {
      const l = links[linkIdx++ % links.length]!;
      items.push({ info: { label: l.label, url: l.url, kind: 'affiliate' }, bg: '#ffffff', fg: '#1b2430', footer: 'amazon.co.uk · affiliate link' });
    } else {
      const hs = HOUSE[houseIdx++ % HOUSE.length]!;
      items.push({ info: { label: hs.label, url: null, kind: 'house' }, bg: hs.bg, fg: hs.fg, footer: '' });
    }
  });

  const textures = new Map<string, THREE.CanvasTexture>();
  slots.forEach((slot, i) => {
    const item = items[i]!;
    const board = new THREE.Group();
    board.position.set(slot.x, 0, slot.z);
    board.rotation.y = slot.rotY + (rng() - 0.5) * 0.02;
    // Frame and legs.
    board.add(box(W + 0.3, H + 0.3, 0.25, '#1b2430', 0, 0.45 + H / 2, -0.1, 0.04));
    board.add(box(0.25, 0.5, 0.25, '#546e7a', -W / 2 + 0.5, 0.25, -0.1, 0.03));
    board.add(box(0.25, 0.5, 0.25, '#546e7a', W / 2 - 0.5, 0.25, -0.1, 0.03));
    const key = `${item.info.kind}:${item.info.label}`;
    let tex = textures.get(key);
    if (!tex) {
      tex = face(item.info.label, item.info.kind, item.bg, item.fg, item.footer);
      textures.set(key, tex);
    }
    // Face on both sides so a board is never a blank frame from any camera.
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(W, H), new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide }));
    panel.position.set(0, 0.45 + H / 2, 0.04);
    const back = new THREE.Mesh(new THREE.PlaneGeometry(W, H), new THREE.MeshBasicMaterial({ map: tex }));
    back.position.set(0, 0.45 + H / 2, -0.26);
    back.rotation.y = Math.PI;
    board.add(back);
    panel.userData = { board: item.info } as { board: BoardInfo };
    board.add(panel);
    panels.push(panel);
    group.add(board);
  });
  return { group, panels };
}
